import React from 'react';
import { Download, Activity, ShieldCheck, BarChart3, Cpu, FileText, ArrowRight, Layers, Zap, ChevronUp } from 'lucide-react';

// --- CUSTOM SOCIAL ICONS (Lucide removed brand logos, so we create them manually!) ---
const FacebookIcon = () => (<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M18 2h-3a5 5 0 0 0-5 5v3H7v4h3v8h4v-8h3l1-4h-4V7a1 1 0 0 1 1-1h3z"></path></svg>);
const TwitterIcon = () => (<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M22 4s-.7 2.1-2 3.4c1.6 10-9.4 17.3-18 11.6 2.2.1 4.4-.6 6-2C3 15.5.5 9.6 3 5c2.2 2.6 5.6 4.1 9 4-.9-4.2 4-6.6 7-3.8 1.1 0 3-1.2 3-1.2z"></path></svg>);
const InstagramIcon = () => (<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="2" y="2" width="20" height="20" rx="5" ry="5"></rect><path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z"></path><line x1="17.5" y1="6.5" x2="17.51" y2="6.5"></line></svg>);
const YoutubeIcon = () => (<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M22.54 6.42a2.78 2.78 0 0 0-1.94-2C18.88 4 12 4 12 4s-6.88 0-8.6.46a2.78 2.78 0 0 0-1.94 2A29 29 0 0 0 1 11.75a29 29 0 0 0 .46 5.33 2.78 2.78 0 0 0 1.94 2C5.12 19.5 12 19.5 12 19.5s6.88 0 8.6-.46a2.78 2.78 0 0 0 1.94-2 29 29 0 0 0 .46-5.33 29 29 0 0 0-.46-5.33z"></path><polygon points="9.75 15.02 15.5 11.75 9.75 8.48 9.75 15.02"></polygon></svg>);
const LinkedinIcon = () => (<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M16 8a6 6 0 0 1 6 6v7h-4v-7a2 2 0 0 0-2-2 2 2 0 0 0-2 2v7h-4v-7a6 6 0 0 1 6-6z"></path><rect x="2" y="9" width="4" height="12"></rect><circle cx="4" cy="4" r="2"></circle></svg>);

export default function DownloadPortal() {
  const scrollToDownloads = () => {
    const target = document.getElementById('downloads');
    if (target) {
      target.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <div style={{ minHeight: '100vh', background: '#0b1121', color: '#f8fafc', fontFamily: 'system-ui, -apple-system, sans-serif', overflowX: 'hidden' }}>
      
      {/* --- INJECTED CSS FOR ANIMATIONS --- */}
      <style>{`
        @keyframes carouselScroll {
          0% { transform: translateX(0); }
          100% { transform: translateX(-50%); }
        }
        .carousel-track {
          display: flex;
          gap: 24px;
          width: max-content;
          animation: carouselScroll 30s linear infinite;
        }
        .carousel-track:hover {
          animation-play-state: paused;
        }
        .glass-card {
          background: rgba(30, 41, 59, 0.4);
          backdrop-filter: blur(12px);
          border: 1px solid rgba(255, 255, 255, 0.05);
          transition: transform 0.3s ease, border-color 0.3s ease;
        }
        .glass-card:hover {
          transform: translateY(-5px);
          border-color: rgba(216, 155, 22, 0.4); /* HUNCH Gold Hover */
        }
        .gradient-text {
          background: linear-gradient(to right, #d89b16, #5f3e00); /* Blue-Purple Gradient */
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
        }
        ::-webkit-scrollbar { width: 8px; }
        ::-webkit-scrollbar-track { background: #0b1121; }
        ::-webkit-scrollbar-thumb { background: #1e293b; border-radius: 4px; }
        ::-webkit-scrollbar-thumb:hover { background: #334155; }
        
        .feature-block {
          display: flex;
          align-items: center;
          gap: 60px;
          margin-bottom: 120px;
          flex-wrap: wrap;
        }
        .feature-block.reverse {
          flex-direction: row-reverse;
        }
        .feature-img-container {
          flex: 1 1 500px;
          border-radius: 16px;
          overflow: hidden;
          border: 1px solid #1e293b;
          box-shadow: 0 25px 50px -12px rgba(0,0,0,0.5);
          background: #121b2f;
        }
        .feature-img-container img {
          width: 100%;
          height: auto;
          display: block;
        }
        .feature-text {
          flex: 1 1 400px;
        }
        .section-header {
          text-align: center;
          margin-bottom: 60px;
        }

        /* Footer Link Styles - Adapted for Golden Background */
        .f-link {
          color: #fef3c7;
          text-decoration: none;
          font-size: 14px;
          display: block;
          margin-bottom: 12px;
          transition: color 0.2s;
        }
        .f-link:hover {
          color: #ffffff;
        }
        .social-icon {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          width: 36px;
          height: 36px;
          border-radius: 50%;
          border: 1px solid #475569;
          color: #94a3b8;
          transition: all 0.2s;
        }
        .social-icon:hover {
          border-color: #f8fafc;
          color: #f8fafc;
          background: rgba(255,255,255,0.1);
        }
      `}</style>

      {/* --- NAVBAR --- */}
      <nav style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '20px 40px', background: 'rgba(11, 17, 33, 0.9)', backdropFilter: 'blur(10px)', position: 'fixed', width: '100%', top: 0, zIndex: 100, boxSizing: 'border-box', borderBottom: '1px solid #1e293b' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <img src={`${process.env.PUBLIC_URL}/hunch_1.png`} alt="HUNCH Logo" style={{ height: '52px' }} />
        </div>
        <div style={{ display: 'flex', gap: '24px', alignItems: 'center' }}>
          <a href="#overview" style={{ color: '#cbd5e1', textDecoration: 'none', fontSize: '14px', fontWeight: '600', transition: 'color 0.2s' }}>Platform</a>
          <a href="#manuals" style={{ color: '#cbd5e1', textDecoration: 'none', fontSize: '14px', fontWeight: '600', transition: 'color 0.2s' }}>Documentation</a>
          <button onClick={scrollToDownloads} style={{ background: '#d89b16', color: '#fff', border: 'none', padding: '10px 24px', borderRadius: '8px', fontSize: '13px', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '1px', cursor: 'pointer', boxShadow: '0 4px 14px 0 rgba(216, 155, 22, 0.3)', transition: 'transform 0.2s' }}>
            Get Software
          </button>
        </div>
      </nav>
      
      {/* --- HERO SECTION --- */}
      <section style={{ 
        minHeight: '100vh', 
        display: 'flex', 
        flexDirection: 'column', 
        justifyContent: 'center', 
        alignItems: 'center', 
        textAlign: 'center', 
        paddingInline: '20px', 
        position: 'relative' 
      }}>
        <div style={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', width: '600px', height: '600px', background: 'radial-gradient(circle, rgba(216,155,22,0.12) 0%, rgba(11,17,33,0) 70%)', zIndex: 0, pointerEvents: 'none' }}></div>
        
        <div style={{ position: 'relative', zIndex: 1, maxWidth: '900px', margin: '0 auto', marginTop: '60px' }}>
          <div style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', background: 'rgba(216, 155, 22, 0.1)', border: '1px solid rgba(216, 155, 22, 0.2)', color: '#f59e0b', padding: '8px 16px', borderRadius: '20px', fontSize: '12px', fontWeight: '700', letterSpacing: '1px', marginBottom: '32px' }}>
            <span style={{ display: 'inline-block', width: '8px', height: '8px', background: '#f59e0b', borderRadius: '50%', boxShadow: '0 0 10px #f59e0b' }}></span>
            MRS9000 TELEMETRY ENGINE v1.0
          </div>
          
          <h1 style={{ fontSize: 'clamp(40px, 6vw, 64px)', fontWeight: '800', margin: '0 0 24px', lineHeight: '1.1', letterSpacing: '-1px' }}>
            Industrial Intelligence.<br />
            <span className="gradient-text">Completely Air Gapped.</span>
          </h1>
          
          <p style={{ fontSize: '18px', color: '#94a3b8', maxWidth: '650px', margin: '0 auto 40px', lineHeight: '1.6' }}>
            A ready to deploy software solution that bridges your Modbus hardware to a real time analytics dashboard stored entirely on your local Database. No cloud, no data leaks.
          </p>
          
          <div style={{ display: 'flex', justifyContent: 'center', gap: '16px', flexWrap: 'wrap' }}>
            <button onClick={scrollToDownloads} style={{ background: '#f8fafc', color: '#0b1121', border: 'none', padding: '16px 32px', borderRadius: '8px', fontSize: '15px', fontWeight: '700', display: 'flex', alignItems: 'center', gap: '10px', cursor: 'pointer', transition: 'transform 0.2s' }}>
              Download Software <ArrowRight size={18} />
            </button>
          </div>
        </div>
      </section>

      {/* --- CAROUSEL SECTION --- */}
      <section style={{ background: '#121b2f', paddingTop: '80px', paddingBottom: '80px', position: 'relative', overflow: 'hidden', borderTop: '1px solid #1e293b', borderBottom: '1px solid #1e293b' }}>
        <div className="section-header">
          <h2 style={{ fontSize: '32px', fontWeight: '800', margin: '0 0 16px', color:"#d89b16"  }}>A Look Inside the Engine</h2>
          <p style={{ color: '#94a3b8', fontSize: '16px' }}>Explore the beautiful, high-performance user interface.</p>
        </div>

        <div style={{ position: 'absolute', left: 0, top: 0, bottom: 0, width: '150px', background: 'linear-gradient(to right, #121b2f, transparent)', zIndex: 2 }}></div>
        <div style={{ position: 'absolute', right: 0, top: 0, bottom: 0, width: '150px', background: 'linear-gradient(to left, #121b2f, transparent)', zIndex: 2 }}></div>
        
        <div className="carousel-track">
          {[1, 2].map((set) => (
            <React.Fragment key={set}>
              <div style={{ width: '800px', height: '450px', borderRadius: '12px', border: '1px solid #1e293b', background: `url('${process.env.PUBLIC_URL}/screen-overview.png') center/contain no-repeat #0b1121`, flexShrink: 0, boxShadow: '0 25px 50px -12px rgba(0,0,0,0.5)' }}></div>
              <div style={{ width: '800px', height: '450px', borderRadius: '12px', border: '1px solid #1e293b', background: `url('${process.env.PUBLIC_URL}/screen-charts.png') center/contain no-repeat #0b1121`, flexShrink: 0, boxShadow: '0 25px 50px -12px rgba(0,0,0,0.5)' }}></div>
              <div style={{ width: '800px', height: '450px', borderRadius: '12px', border: '1px solid #1e293b', background: `url('${process.env.PUBLIC_URL}/screen-provision.png') center/contain no-repeat #0b1121`, flexShrink: 0, boxShadow: '0 25px 50px -12px rgba(0,0,0,0.5)' }}></div>
            </React.Fragment>
          ))}
        </div>
      </section>

      {/* --- ALTERNATING BLOCKS SECTION --- */}
      <section id="overview" style={{ padding: '120px 20px', maxWidth: '1200px', margin: '0 auto', background: '#0b1121' }}>
        
        <div className="section-header">
          <h2 style={{ fontSize: '32px', fontWeight: '800', margin: '0 0 16px', color:"#d89b16"  }}>Platform Deep Dive</h2>
          <p style={{ color: '#94a3b8', fontSize: '18px', maxWidth: '600px', margin: '0 auto' }}>Discover how the MRS9000 seamlessly translates raw hardware data into visual factory intelligence.</p>
        </div>

        <div className="feature-block">
          <div className="feature-img-container">
            <img src={`${process.env.PUBLIC_URL}/add_b.png`} alt="Dashboard Overview" />
          </div>
          <div className="feature-text">
            <div style={{ display: 'inline-flex', padding: '10px', background: 'rgba(56, 189, 248, 0.1)', color: '#38bdf8', borderRadius: '10px', marginBottom: '20px' }}><Layers size={24} /></div>
            <h2 style={{ fontSize: '32px', fontWeight: '800', margin: '0 0 16px' }}>Map Your Entire Facility</h2>
            <p style={{ color: '#94a3b8', fontSize: '18px', lineHeight: '1.6', margin: '0 0 24px' }}>
              Don't just look at a spreadsheet. The MRS9000 allows you to visually map your factory from the top down. Create Buildings, drill down into Departments, and organize your telemetry nodes exactly how they sit on the production floor.
            </p>
            <ul style={{ listStyle: 'none', padding: 0, margin: 0, color: '#cbd5e1', display: 'flex', flexDirection: 'column', gap: '12px' }}>
              <li style={{ display: 'flex', alignItems: 'center', gap: '10px' }}><ShieldCheck size={18} color="#10b981"/> Admin & User permission levels</li>
              <li style={{ display: 'flex', alignItems: 'center', gap: '10px' }}><ShieldCheck size={18} color="#10b981"/> Visual card-based navigation</li>
            </ul>
          </div>
        </div>

        <div className="feature-block reverse">
          <div className="feature-img-container">
            <img src={`${process.env.PUBLIC_URL}/screen-charts.png`} alt="Analytics Charts" />
          </div>
          <div className="feature-text">
            <div style={{ display: 'inline-flex', padding: '10px', background: 'rgba(16, 185, 129, 0.1)', color: '#10b981', borderRadius: '10px', marginBottom: '20px' }}><BarChart3 size={24} /></div>
            <h2 style={{ fontSize: '32px', fontWeight: '800', margin: '0 0 16px' }}>Real-Time Intelligence</h2>
            <p style={{ color: '#94a3b8', fontSize: '18px', lineHeight: '1.6', margin: '0 0 24px' }}>
              Transform raw Modbus registers into beautiful, actionable insights. Watch your live power consumption, flow rates, and temperatures plot dynamically on Charts rendering engines without any network lag.
            </p>
            <ul style={{ listStyle: 'none', padding: 0, margin: 0, color: '#cbd5e1', display: 'flex', flexDirection: 'column', gap: '12px' }}>
              <li style={{ display: 'flex', alignItems: 'center', gap: '10px' }}><ShieldCheck size={18} color="#10b981"/> Automated High/Low extreme tracking</li>
              <li style={{ display: 'flex', alignItems: 'center', gap: '10px' }}><ShieldCheck size={18} color="#10b981"/> 1-Click PDF and CSV Exports</li>
            </ul>
          </div>
        </div>

        <div className="feature-block" style={{ marginBottom: 0 }}>
          <div className="feature-img-container">
            <img src={`${process.env.PUBLIC_URL}/add_d.png`} alt="Hardware Provisioning" />
          </div>
          <div className="feature-text">
            <div style={{ display: 'inline-flex', padding: '10px', background: 'rgba(245, 158, 11, 0.1)', color: '#f59e0b', borderRadius: '10px', marginBottom: '20px' }}><Zap size={24} /></div>
            <h2 style={{ fontSize: '32px', fontWeight: '800', margin: '0 0 16px' }}>Instant Node Deployment</h2>
            <p style={{ color: '#94a3b8', fontSize: '18px', lineHeight: '1.6', margin: '0 0 24px' }}>
              Adding a new device to your network shouldn't require a software developer. Our rapid provisioning engine lets you enter an IP address, map your registers, and start logging data in under 60 seconds.
            </p>
            <ul style={{ listStyle: 'none', padding: 0, margin: 0, color: '#cbd5e1', display: 'flex', flexDirection: 'column', gap: '12px' }}>
              <li style={{ display: 'flex', alignItems: 'center', gap: '10px' }}><ShieldCheck size={18} color="#10b981"/> Hardware Agnostic Network Support</li>
              <li style={{ display: 'flex', alignItems: 'center', gap: '10px' }}><ShieldCheck size={18} color="#10b981"/> Automatic Database Table Generation</li>
            </ul>
          </div>
        </div>
      </section>

      {/* --- GRID FEATURES SECTION --- */}
      <section style={{ padding: '100px 20px', background: '#0f172a', borderTop: '1px solid #1e293b', borderBottom: '1px solid #1e293b' }}>
        <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
          
          <div className="section-header">
            <h2 style={{ fontSize: '32px', fontWeight: '800', margin: '0 0 16px', color:"#d89b16"  }}>Core System Capabilities</h2>
            <p style={{ color: '#94a3b8', fontSize: '16px' }}>Engineered specifically for the demands of industrial IT.</p>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '30px' }}>
            <div className="glass-card" style={{ padding: '40px', borderRadius: '16px' }}>
              <div style={{ width: '50px', height: '50px', background: 'rgba(14, 165, 233, 0.1)', color: '#0ea5e9', borderRadius: '12px', display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: '24px' }}><Activity size={24} /></div>
              <h3 style={{ fontSize: '20px', fontWeight: '700', marginBottom: '16px' }}>5-Second Polling</h3>
              <p style={{ color: '#94a3b8', lineHeight: '1.6', margin: 0 }}>A robust Node.js background engine continuously polls your hardware to guarantee data integrity, even when the UI is closed.</p>
            </div>
            
            <div className="glass-card" style={{ padding: '40px', borderRadius: '16px' }}>
              <div style={{ width: '50px', height: '50px', background: 'rgba(16, 185, 129, 0.1)', color: '#10b981', borderRadius: '12px', display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: '24px' }}><ShieldCheck size={24} /></div>
              <h3 style={{ fontSize: '20px', fontWeight: '700', marginBottom: '16px' }}>Offline Security</h3>
              <p style={{ color: '#94a3b8', lineHeight: '1.6', margin: 0 }}>Designed for strict intranet environments. Cryptographically-signed offline licensing binds the software directly to your host PC's silicon.</p>
            </div>
            
            <div className="glass-card" style={{ padding: '40px', borderRadius: '16px' }}>
              <div style={{ width: '50px', height: '50px', background: 'rgba(139, 92, 246, 0.1)', color: '#8b5cf6', borderRadius: '12px', display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: '24px' }}><Cpu size={24} /></div>
              <h3 style={{ fontSize: '20px', fontWeight: '700', marginBottom: '16px' }}>Hardware Agnostic</h3>
              <p style={{ color: '#94a3b8', lineHeight: '1.6', margin: 0 }}>Whether you are using off-the-shelf PLC gateways or custom ESP32 networks, the MRS9000 integrates seamlessly via standard Modbus.</p>
            </div>
          </div>
        </div>
      </section>

      {/* --- MANUALS SECTION --- */}
      <section id="manuals" style={{ padding: '120px 20px', background: '#0b1121' }}>
        <div style={{ maxWidth: '800px', margin: '0 auto' }}>
          <h2 style={{ fontSize: '32px', fontWeight: '800', marginBottom: '40px', textAlign: 'center', color:"#d89b16" }}>System Documentation</h2>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
            
            <a href={`${process.env.PUBLIC_URL}/downloads/MRS9000_Brochure.pdf`} download="MRS9000_Brochure.pdf" 
               style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', background: '#121b2f', padding: '24px 32px', borderRadius: '16px', border: '1px solid #1e293b', textDecoration: 'none', color: '#f8fafc', transition: 'border-color 0.2s', boxShadow: '0 10px 30px -10px rgba(0,0,0,0.5)' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '20px' }}>
                <div style={{ padding: '16px', background: 'rgba(216, 155, 22, 0.1)', borderRadius: '12px' }}><FileText size={28} color="#d89b16" /></div>
                <div>
                  <h4 style={{ margin: '0 0 8px', fontSize: '18px', fontWeight: '700' }}>MRS9000 Product Brochure</h4>
                  <p style={{ margin: 0, color: '#94a3b8', fontSize: '14px' }}>Complete feature overview, system architecture, and SCADA specifications.</p>
                </div>
              </div>
              <Download size={24} color="#d89b16" />
            </a>

            <a href={`${process.env.PUBLIC_URL}/downloads/MRS9000_AdminGuide.pdf`} download="MRS9000_AdminGuide.pdf" 
               style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', background: '#121b2f', padding: '24px 32px', borderRadius: '16px', border: '1px solid #1e293b', textDecoration: 'none', color: '#f8fafc', transition: 'border-color 0.2s', boxShadow: '0 10px 30px -10px rgba(0,0,0,0.5)' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '20px' }}>
                <div style={{ padding: '16px', background: 'rgba(216, 155, 22, 0.1)', borderRadius: '12px' }}><ShieldCheck size={28} color="#d89b16" /></div>
                <div>
                  <h4 style={{ margin: '0 0 8px', fontSize: '18px', fontWeight: '700' }}>Offline Activation Guide</h4>
                  <p style={{ margin: 0, color: '#94a3b8', fontSize: '14px' }}>How to securely generate your hardware key and license the software.</p>
                </div>
              </div>
              <Download size={24} color="#d89b16" />
            </a>

          </div>
        </div>
      </section>

      {/* --- DOWNLOAD CTA SECTION (Now Dark Theme) --- */}
      <section id="downloads" style={{ padding: '100px 20px', background: '#0f172a', borderTop: '1px solid #1e293b', textAlign: 'center' }}>
        <div style={{ maxWidth: '800px', margin: '0 auto' }}>
          <h2 style={{ fontSize: '40px', fontWeight: '800', margin: '0 0 20px', color: '#d89b16' }}>Ready to Digitize Your Factory?</h2>
          <p style={{ fontSize: '18px', color: '#94a3b8', margin: '0 auto 40px', maxWidth: '500px' }}>
            Download the MRS9000 executable today. Includes a fully functional 7-Day Free Evaluation.
          </p>
          
          <a href={`${process.env.PUBLIC_URL}/downloads/MRS9000_Setup.exe`} download="MRS9000_Setup.exe" style={{ display: 'inline-flex', alignItems: 'center', gap: '12px', background: '#d89b16', color: '#fff', padding: '20px 40px', borderRadius: '12px', fontSize: '18px', fontWeight: '800', textDecoration: 'none', boxShadow: '0 10px 25px -5px rgba(216, 155, 22, 0.3)', transition: 'transform 0.2s' }}
             onMouseOver={(e) => e.currentTarget.style.transform = 'translateY(-2px)'}
             onMouseOut={(e) => e.currentTarget.style.transform = 'translateY(0)'}>
            <Download size={24} /> Download MRS9000
          </a>
          <p style={{ marginTop: '20px', color: '#64748b', fontSize: '13px' }}>Version 1.0.4 • Windows 10/11 64-bit • 108MB</p>
        </div>
      </section>

      {/* --- RE-ENGINEERED CORPORATE FOOTER (Now with the Golden Gradient!) --- */}
      <footer style={{ background: '#000000', color: '#f8fafc', padding: '0' }}>
        
       {/* Main Footer Links Area */}
        <div style={{ background: 'linear-gradient(to bottom, #d89b16, #000)', padding: '60px 40px' }}>
          <div style={{ maxWidth: '1200px', margin: '0 auto', display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '40px' }}>
            
            {/* Column 1: Logo & Branding */}
            <div>
              <img src={`${process.env.PUBLIC_URL}/hunch_white.png`} alt="HUNCH Logo" style={{ height: '48px', marginBottom: '20px', display: 'block' }} />
              <p style={{ color: '#fef3c7', fontSize: '13px', lineHeight: '1.6', margin: 0 }}>
                Building simple and powerful monitoring dashboards tailored to enterprise needs.
              </p>
            </div>

            {/* Column 2: Useful Links */}
           <div>
              {/* ✨ FIXED: Changed margin to '0 0 20px 0' to remove the invisible top gap */}
              <h4 style={{ color: '#fff', fontSize: '14px', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '1px', margin: '0 0 20px 0' }}>Useful Links</h4>
              <a href="/" className="f-link">Home</a>
              <a href="/#overview" className="f-link">Platform</a>
              <a href="/#manuals" className="f-link">Documentation</a>
              <a href="/#downloads" className="f-link">Get Software</a>
            </div>

            {/* Column 3: Terms & Conditions */}
            <div>
              <h4 style={{ color: '#fff', fontSize: '14px', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '1px', margin: '0 0 20px 0' }}>Terms & Conditions</h4>
              <a href="/terms-of-use" className="f-link">Terms of use</a>
              <a href="/privacy-policy" className="f-link">Privacy Policy</a>            </div>

            {/* Column 4: Get to Know Us */}
            <div>
              <h4 style={{ color: '#fff', fontSize: '14px', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '1px', margin: '0 0 20px 0' }}>Get To Know Us</h4>
     
<a href="/contact-us" className="f-link">Contact Us</a> {/* ✨ Updated multi-page link */}            </div>

          </div>
        </div>
{/* Bottom Copyright Bar */}
        <div style={{ background: '#000', padding: '20px 40px', position: 'relative', borderTop: '1px solid #d89b16' }}>
        {/* Bottom Copyright Bar */}
        
          <div style={{ maxWidth: '1200px', margin: '0 auto', display: 'flex', flexWrap: 'wrap', justifyContent: 'space-between', alignItems: 'center', gap: '20px' }}>
            
            <div style={{ fontSize: '13px', color: '#94a3b8' }}>
              Copyright &copy; 2006 - 2026 <strong>HUNCH Automation (Pvt.) Ltd.</strong> | All Rights Reserved
            </div>
            
            <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
              <span style={{ fontSize: '13px', color: '#f8fafc', fontWeight: '600' }}>Follow Us:</span>
              <a href="https://www.facebook.com/cloud.hunch" target="_blank" rel="noopener noreferrer" className="social-icon"><FacebookIcon /></a>
              <a href="https://x.com/HunchCloud" target="_blank" rel="noopener noreferrer" className="social-icon"><TwitterIcon /></a>
              <a href="https://www.instagram.com/hunchcloud/" target="_blank" rel="noopener noreferrer" className="social-icon"><InstagramIcon /></a>
              <a href="https://www.youtube.com/" target="_blank" rel="noopener noreferrer" className="social-icon"><YoutubeIcon /></a>
              <a href="https://www.linkedin.com/company/hunch-cloud/" target="_blank" rel="noopener noreferrer" className="social-icon"><LinkedinIcon /></a>
            </div>

          </div>

          {/* Floating Back to Top Button */}
          <button 
            onClick={scrollToTop}
            style={{ position: 'absolute', right: '40px', top: '-24px', width: '48px', height: '48px', borderRadius: '50%', background: '#d89b16', color: '#000', border: 'none', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', boxShadow: '0 4px 12px rgba(0,0,0,0.5)', transition: 'transform 0.2s' }}
            onMouseOver={(e) => e.currentTarget.style.transform = 'translateY(-4px)'}
            onMouseOut={(e) => e.currentTarget.style.transform = 'translateY(0)'}
            aria-label="Back to top"
          >
            <ChevronUp size={24} />
          </button>
        </div>
      </footer>

    </div>
  );
}